"use client";

import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { PostHogProvider } from "posthog-js/react";

import { cutGluedReturnParams, STRIPE_RETURN_PARAMS } from "@ergon-ai/utils";

import { useConsent } from "../consent/ConsentProvider";
import { posthog, whenPostHogReady } from "./posthog.client";

/**
 * Exact names, not the substring rule below: `payment_intent`, `setup_intent`
 * and `redirect_status` carry no "secret" (only their `_client_secret`
 * siblings do), so they used to reach PostHog unredacted off a checkout
 * return (EAH-2525). Same list Sentry redacts in `../sentry/redact-url.ts`.
 */
const STRIPE_RETURN_PARAM_SET: ReadonlySet<string> = new Set(
  STRIPE_RETURN_PARAMS,
);

/**
 * PostHog provider component for Next.js App Router
 */
function PostHogProviderWrapper({ children }: { children: React.ReactNode }) {
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

/**
 * Page view tracking component
 */
function PageViewTracker({ currentLocale }: { currentLocale?: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { allows } = useConsent();
  const analyticsAllowed = allows("analytics");
  // A pageview is one PAGE, not one URL. Params change without navigation —
  // the waitlist strips ?confirmed=1 after handling it, and any code that
  // cleans or adds a param does the same — and each change re-ran this effect,
  // reporting a second pageview for a visit that never moved. The reported
  // event has never included the query string, so those were pure duplicates.
  const lastReported = useRef<string | null>(null);

  useEffect(() => {
    const params: Record<string, string> = {};
    const sensitiveParams = ["email", "token", "password", "key", "secret"];

    searchParams.forEach((value, key) => {
      // Filter out sensitive parameters for GDPR compliance, plus the exact
      // Stripe return names (STRIPE_RETURN_PARAMS): a substring rule alone
      // never catches those, since only their `_client_secret` variants
      // contain "secret".
      if (
        !STRIPE_RETURN_PARAM_SET.has(key) &&
        !sensitiveParams.some((sensitive) =>
          key.toLowerCase().includes(sensitive.toLowerCase()),
        )
      ) {
        // A name filter cannot see a secret glued inside another param's value.
        params[key] = cutGluedReturnParams(value);
      }
    });

    if (typeof window === "undefined") return;
    // Before the queue, not after it. `whenPostHogReady` HOLDS callbacks until
    // init instead of dropping them, so a visitor who browses three pages and
    // then accepts would have all three replayed at once. Consent is not
    // retroactive, so the pages seen before the yes are never reported. This
    // effect re-runs on the grant, which reports the page they are on now.
    if (!analyticsAllowed) return;
    if (lastReported.current === pathname) return;

    // Through the ready queue, not a `posthog.__loaded` check. This effect is a
    // CHILD of the provider that calls init, and React runs child effects first,
    // so on the very first render __loaded is false and a plain check has
    // nothing to retry with — the landing pageview of a visitor who never
    // navigates was simply lost. That is the first step of the funnel, so the
    // conversion could exist with no visit in front of it.
    whenPostHogReady((client) => {
      // Re-checked inside: the callback can run later than the effect, and a
      // navigation in between must not produce two pageviews for one path.
      if (lastReported.current === pathname) return;
      lastReported.current = pathname;
      client.capture("$pageview", {
        $current_url: `${window.location.origin}${pathname}`,
        pageName: pathname,
        userLanguage: currentLocale,
        platform: "web",
        ...params,
      });
    });
  }, [pathname, searchParams, currentLocale, analyticsAllowed]);

  return null;
}

/**
 * Client component to initialize unified analytics and track page views
 */
export function AnalyticsProvider({
  children,
  currentLocale,
}: {
  children: React.ReactNode;
  currentLocale?: string;
}) {
  return (
    <PostHogProviderWrapper>
      {/* The tracker renders nothing, so its fallback must render nothing:
          useSearchParams suspends under cacheComponents, and a visible
          fallback here paints a stray "Loading..." above the page for every
          visitor arriving with query params (ads, the DOI redirect). */}
      <Suspense fallback={null}>
        <PageViewTracker currentLocale={currentLocale} />
      </Suspense>
      {children}
    </PostHogProviderWrapper>
  );
}
